// frontend/src/contexts/RealTimeContext.jsx
// Real-time notifications and updates context

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { useNotifications } from "./NotificationContext";
import { getApiBaseUrl } from "../config/api";
import { DEBUG_ENABLED } from "../utils/consoleFilter";

const RealTimeContext = createContext();

export const useRealTime = () => {
  const context = useContext(RealTimeContext);
  if (!context) {
    throw new Error("useRealTime must be used within a RealTimeProvider");
  }
  return context;
};

export const RealTimeProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { showSuccess, showError, showInfo, showWarning } = useNotifications();
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [systemStatus, setSystemStatus] = useState("idle");
  const [activeScrapes, setActiveScrapes] = useState(0);
  const [newDeals, setNewDeals] = useState([]);
  const [priceChanges, setPriceChanges] = useState([]);
  const [errorCount, setErrorCount] = useState(0);
  const [ws, setWs] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [maxReconnectAttempts] = useState(2);

  // Initialize WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!isAuthenticated || !user) return;

    // Skip WebSocket for now since backend doesn't support it yet
    if (DEBUG_ENABLED) {
      console.log("WebSocket disabled - backend not ready, using polling only");
    }
    return;

    // Check if WebSocket is supported
    if (!window.WebSocket) {
      if (DEBUG_ENABLED) {
        console.log("WebSocket not supported, using polling only");
      }
      return;
    }

    // Only attempt WebSocket if we haven't already tried too many times
    if (reconnectAttempts >= maxReconnectAttempts) {
      if (DEBUG_ENABLED) {
        console.log("Skipping WebSocket connection - max attempts reached");
      }
      return;
    }

    // Attempt WebSocket connection
    try {
      const wsUrl = `${getApiBaseUrl().replace("http", "ws")}/ws/notifications`;
      if (DEBUG_ENABLED) {
        console.log("Attempting WebSocket connection to:", wsUrl);
      }

      const websocket = new WebSocket(wsUrl);

      websocket.onopen = () => {
        setIsConnected(true);
        setReconnectAttempts(0);
        if (DEBUG_ENABLED) {
          console.log("WebSocket connected successfully");
        }
      };

      websocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleRealTimeMessage(data);
        } catch (error) {
          if (DEBUG_ENABLED) {
            console.error("Error parsing WebSocket message:", error);
          }
        }
      };

      websocket.onclose = (event) => {
        setIsConnected(false);
        if (DEBUG_ENABLED) {
          console.log("WebSocket disconnected:", event.code, event.reason);
        }

        // Only attempt reconnection if we haven't reached max attempts
        if (reconnectAttempts < maxReconnectAttempts - 1) {
          if (DEBUG_ENABLED) {
            console.log(
              `Attempting to reconnect (${
                reconnectAttempts + 1
              }/${maxReconnectAttempts})...`
            );
          }
          setTimeout(() => {
            setReconnectAttempts((prev) => prev + 1);
            connectWebSocket();
          }, 5000); // Fixed delay instead of exponential backoff
        } else {
          if (DEBUG_ENABLED) {
            console.log(
              "Max reconnection attempts reached, falling back to polling"
            );
          }
        }
      };

      websocket.onerror = (error) => {
        if (DEBUG_ENABLED) {
          console.log(
            "WebSocket connection failed, falling back to polling:",
            error
          );
        }
        // Don't show error notification - just log and fall back to polling
      };

      setWs(websocket);
    } catch (error) {
      if (DEBUG_ENABLED) {
        console.log(
          "Error creating WebSocket, falling back to polling:",
          error
        );
      }
      // Don't show error notification - just log and fall back to polling
    }
  }, [
    isAuthenticated,
    user,
    reconnectAttempts,
    maxReconnectAttempts,
    showSuccess,
    showWarning,
    showError,
  ]);

  // Handle real-time messages
  const handleRealTimeMessage = useCallback(
    (data) => {
      setLastUpdate(new Date());

      switch (data.type) {
        case "system_status":
          setSystemStatus(data.status);
          if (data.status === "error") {
            showError(
              "System Error",
              data.message || "System encountered an error"
            );
          }
          break;

        case "scraper_started":
          setActiveScrapes((prev) => prev + 1);
          showInfo("Scraper Started", "Price monitoring has begun");
          break;

        case "scraper_completed":
          setActiveScrapes((prev) => Math.max(0, prev - 1));
          showSuccess(
            "Scraper Completed",
            `Found ${data.deals_count || 0} new deals`
          );
          break;

        case "new_deal":
          setNewDeals((prev) => [data.deal, ...prev.slice(0, 9)]); // Keep last 10
          showSuccess(
            "New Deal Found!",
            `${data.deal.name} - ${data.deal.discount}% off`
          );
          break;

        case "price_change":
          setPriceChanges((prev) => [data.change, ...prev.slice(0, 9)]); // Keep last 10
          const changeType =
            data.change.type === "increase" ? "increased" : "decreased";
          showInfo(
            "Price Change",
            `${data.change.item_name} price ${changeType} to ${data.change.new_price}`
          );
          break;

        case "error":
          setErrorCount((prev) => prev + 1);
          showError(
            "Scraping Error",
            data.message || "An error occurred during scraping"
          );
          break;

        case "maintenance":
          showWarning(
            "Maintenance Notice",
            data.message || "System maintenance in progress"
          );
          break;

        default:
          console.log("Unknown real-time message type:", data.type);
      }
    },
    [showSuccess, showError, showInfo, showWarning]
  );

  // Polling fallback for when WebSocket is not available
  const startPolling = useCallback(() => {
    if (!isAuthenticated) return;

    const pollInterval = setInterval(async () => {
      try {
        // For now, just update the last update time
        // The actual system status endpoint doesn't exist yet
        setLastUpdate(new Date());
        if (DEBUG_ENABLED) {
          console.log("Polling for system updates...");
        }
      } catch (error) {
        if (DEBUG_ENABLED) {
          console.log(
            "Polling error (expected until backend is ready):",
            error
          );
        }
      }
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(pollInterval);
  }, [isAuthenticated]);

  // Connect when user authenticates
  useEffect(() => {
    if (isAuthenticated && user) {
      connectWebSocket();
      const pollCleanup = startPolling();

      return () => {
        if (ws) {
          ws.close();
        }
        if (pollCleanup) {
          pollCleanup();
        }
      };
    }
  }, [isAuthenticated, user, connectWebSocket, startPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [ws]);

  // Send message through WebSocket
  const sendMessage = useCallback(
    (message) => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(message));
      }
    },
    [ws]
  );

  // Manual reconnect
  const reconnect = useCallback(() => {
    if (ws) {
      ws.close();
    }
    setReconnectAttempts(0);
    connectWebSocket();
  }, [ws, connectWebSocket]);

  // Clear notifications
  const clearNewDeals = useCallback(() => {
    setNewDeals([]);
  }, []);

  const clearPriceChanges = useCallback(() => {
    setPriceChanges([]);
  }, []);

  const clearErrorCount = useCallback(() => {
    setErrorCount(0);
  }, []);

  const value = {
    // Connection state
    isConnected,
    reconnect,
    sendMessage,

    // Real-time data
    lastUpdate,
    systemStatus,
    activeScrapes,
    newDeals,
    priceChanges,
    errorCount,

    // Actions
    clearNewDeals,
    clearPriceChanges,
    clearErrorCount,
  };

  return (
    <RealTimeContext.Provider value={value}>
      {children}
    </RealTimeContext.Provider>
  );
};
